/**
 * Zentrale Per-Lektion-Status-Stile für den Kaskaden-Hover (TeacherBadge Tooltip).
 *
 * Einzige Quelle für die Zeilen-/Schrift-Farbe einer Lektion in der Hover-Tabelle.
 * Siehe /plans/colors-fix-v2.md §2 (Farb-Semantik).
 *
 * Status-Bedeutung:
 * - confirmed:          Lektion definitiv diesem Lehrer übergeben (grün FETT)
 * - could_cover:        Reserve-Kandidat könnte übernehmen (neutral, nicht angenommen)
 * - waiting:            interessiert / Wartepool (provisorisch) -> blau
 * - declined:           abgelehnt / nicht interessiert -> rot
 * - not_available:      nicht verfügbar (Reserve-Kandidat, Matcher) -> rot
 * - pending_active:     angefragt, Timeout läuft noch -> orange + orange Schrift
 * - pending_timeout:    keine Antwort, kann noch annehmen -> orange + rote Schrift
 * - assigned_elsewhere: an anderen Lehrer vergeben -> grau
 * - cancelled:          Lektion storniert -> grau
 */
export interface LessonStatusStyle {
  row: string
  text: string
  label: string
}

export const LESSON_STATUS_STYLES: Record<string, LessonStatusStyle> = {
  confirmed: {
    row: 'bg-green-50 dark:bg-green-900/20',
    text: 'text-green-700 dark:text-green-400 font-bold',
    label: 'Übernommen',
  },
  could_cover: {
    row: 'bg-gray-50 dark:bg-gray-800',
    text: 'text-gray-700 dark:text-gray-300',
    label: 'Könnte übernehmen',
  },
  waiting: {
    row: 'bg-blue-50 dark:bg-blue-900/20',
    text: 'text-blue-700 dark:text-blue-400',
    label: 'Wartepool',
  },
  declined: {
    row: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-800 dark:text-red-300',
    label: 'Abgelehnt / nicht interessiert',
  },
  not_available: {
    row: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-800 dark:text-red-300',
    label: 'Nicht verfügbar',
  },
  pending_active: {
    row: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-800 dark:text-orange-300',
    label: 'Angefragt, wartet auf Antwort',
  },
  pending_timeout: {
    row: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-red-700 dark:text-red-400',
    label: 'Keine Antwort, kann noch annehmen',
  },
  assigned_elsewhere: {
    row: 'bg-gray-100 dark:bg-gray-700/40',
    text: 'text-gray-600 dark:text-gray-400',
    label: 'An andere Lehrperson vergeben',
  },
  cancelled: {
    row: 'bg-gray-200 dark:bg-gray-700/40',
    text: 'text-gray-500 dark:text-gray-400',
    label: 'Storniert',
  },
}

const FALLBACK_STYLE: LessonStatusStyle = {
  row: 'bg-gray-50 dark:bg-gray-800',
  text: 'text-gray-700 dark:text-gray-300',
  label: 'Unbekannt',
}

/**
 * Liefert die Style-Definition für einen Lektions-Status.
 */
export function getLessonStatusStyle(status: string | null | undefined): LessonStatusStyle {
  if (status && LESSON_STATUS_STYLES[status]) {
    return LESSON_STATUS_STYLES[status]
  }
  return FALLBACK_STYLE
}

interface LessonLike {
  status?: string
  covered?: boolean
  assignment_state?: string
}

/**
 * Löst den Status einer Lektion auf – mit Back-Compat für ältere Responses,
 * die nur covered/missing (covered: boolean) oder assignment_state liefern.
 *
 * - status vorhanden -> direkt verwenden
 * - sonst: assignment_state -> entsprechenden lesson-status mappen
 * - sonst: covered === true -> could_cover (Verfügbarkeit, nicht Annahme)
 *          covered === false -> not_available (altes Rot)
 */
export function resolveLessonStatus(lesson: LessonLike): string {
  if (lesson.status) return lesson.status
  if (lesson.assignment_state) {
    switch (lesson.assignment_state) {
      case 'waiting': return 'waiting'
      case 'accepted':
      case 'assigned': return 'confirmed'
      case 'declined': return 'declined'
      case 'cancelled': return 'cancelled'
      default: break
    }
  }
  if (lesson.covered === true) return 'could_cover'
  if (lesson.covered === false) return 'not_available'
  return 'pending_timeout'
}
